'use client';

import { Spinner } from '@/components/glitch-ui/spinner';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { NotFoundPage } from '@/components/not-found/not-found-page';
import { useState, createContext, useContext, useEffect, useRef } from 'react';

import { SidebarProvider } from '@/components/ui/sidebar';
import { NavigationSidebar } from '@/components/navigation-sidebar';
import { useNotificationQuery } from '@/lib/query-options';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';

interface NotificationContextType {
  selectedNotificationItemId: string;
  setSelectedNotificationItemId: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  return context;
};

export const Route = createFileRoute('/_authenticated/_layout')({
  component: () => {
    const location = useLocation();
    const [selectedNotificationItemId, setSelectedNotificationItemId] = useState<string>('');
    const mainRef = useRef<HTMLElement>(null);
    const organizationData = useAtomValue(organizationDataAtom);
    const { isSwitching } = useOrganizationInitialization();

    const { data: notificationData } = useNotificationQuery({ organization_id: organizationData?.id, is_archived: false }, 1, 100);

    const handleNotificationSelect = (id: string) => {
      setSelectedNotificationItemId(id);
    };

    useEffect(() => {
      const hasNotificationIdInUrl = location.pathname.match(/\/notifications\/(\d+)/);
      const showNotifications = location.pathname.startsWith('/notifications');
      if (showNotifications && notificationData?.items?.length && !selectedNotificationItemId && !hasNotificationIdInUrl) {
        const nonArchivedNotifications = notificationData.items.filter(item => !item.is_archived);
        if (nonArchivedNotifications.length > 0) {
          const topNotification = nonArchivedNotifications[0];
          handleNotificationSelect(topNotification.id.toString());
        }
      }
    }, [notificationData, selectedNotificationItemId, location.pathname]);

    useEffect(() => {
      const logMainWidth = () => {
        if (mainRef.current) {
          // Log width for debugging if needed
          // const width = mainRef.current.offsetWidth;
        }
      };

      logMainWidth(); // Log initial width
      window.addEventListener('resize', logMainWidth);

      return () => {
        window.removeEventListener('resize', logMainWidth);
      };
    }, []);

    return (
      <NotificationContext.Provider value={{ selectedNotificationItemId, setSelectedNotificationItemId }}>
        {/* The stacked icon+label rail is THE nav — no expanded variant. */}
        <SidebarProvider open={false}>
          <div className='flex w-full h-screen overflow-hidden'>
            <NavigationSidebar />
            <div className='flex flex-col ml-0 w-full min-w-0 h-screen relative'>
              {/* #6477: during an org switch the page tree is UNMOUNTED, not just
                  covered — a mounted route would refetch org-scoped (or now
                  foreign) data mid-purge and throw 404s into error boundaries. */}
              {isSwitching ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-white transition-opacity">
                  <div className="flex items-center gap-3">
                    <Spinner size="lg" className="text-[#290BD9]" />
                    <span className="text-sm font-medium text-gray-500">Switching organization...</span>
                  </div>
                </div>
              ) : (
                <main ref={mainRef} className='flex-1 overflow-y-scroll' style={{minWidth: '512px'}}>
                  <div className='flex justify-center'>
                    <div className='flex w-full min-w-0 h-full flex-col space-y-6 ml-1 mr-1.5 pb-[100px]'>
                      <Outlet />
                    </div>
                  </div>
                </main>
              )}
            </div>
            {/* <Chatbar collapsed={rightCollapsed} setCollapsed={setRightCollapsed} /> */}
          </div>
        </SidebarProvider>
      </NotificationContext.Provider>
    );
  },
  notFoundComponent: () => <NotFoundPage />,
});
